import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { P2GAlert, P2GBox, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { useEffect, useState } from 'react';
import useOnLoad from '~/routes/use-on-load';
import { ActionType } from '~/lib/api/account-linking/types';

type props = {
  onAttempt: () => void;
  integrationSlug?: string;
  action?: ActionType;
  pleaseNoteText?: string;
};

const Verify: React.FC<props> = ({ onAttempt, integrationSlug, action, pleaseNoteText }) => {
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const [attempt, setAttempt] = useState(0);

  // Every 10 seconds, attempt to verify the account until we get a successful response, which will be handled by the parent component.
  // A pleaseNoteText means DTDC has already returned a definitive document rejection, so stop polling - the customer needs to act, not wait.
  useEffect(() => {
    if (pleaseNoteText) {
      return;
    }
    const interval = setInterval(() => {
      setAttempt((prev) => prev + 1);
      onAttempt();
    }, 10000);
    return () => clearInterval(interval);
  }, [onAttempt, pleaseNoteText]);

  useOnLoad(() => {
    if (pleaseNoteText) {
      return;
    }
    setAttempt(1);
    onAttempt();
  });

  if (pleaseNoteText) {
    return (
      <P2GBox textAlign="center" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <P2GAlert color="error">
          <P2GText component="p">
            {t(`${integrationSlug}_${action}_PLEASE_NOTE`.toUpperCase(), {
              defaultValue: pleaseNoteText
            })}
          </P2GText>
        </P2GAlert>
        <P2GText>
          {t('KNOW_YOUR_CUSTOMER_VERIFY_REJECTED', {
            defaultValue: 'Verification was unsuccessful. Please cancel this application and start again.'
          })}
        </P2GText>
      </P2GBox>
    );
  }

  return (
    <P2GBox textAlign="center" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <P2GBox>
        <P2GProgress size="large" />
      </P2GBox>
      <P2GText>
        {t('KNOW_YOUR_CUSTOMER_VERIFY_IN_PROGRESS', {
          defaultValue: 'We are verifying your account. This might take a few moments.'
        })}
      </P2GText>
      <P2GText>
        {t('KNOW_YOUR_CUSTOMER_VERIFY_IN_PROGRESS_SUB_CONTENT', {
          defaultValue: 'In some cases it could take up to 8 hours.'
        })}
      </P2GText>
      <P2GText variant="caption" mt={2} sx={{ color: 'wheat' }}>
        {t('ATTEMPT', { defaultValue: 'Attempt' })}: {attempt}
      </P2GText>
    </P2GBox>
  );
};

export default Verify;
